/**
 * "Other" status-update recipients — an auto-growing list: each row is a person
 * (directory autocomplete) or a custom email. Picking from the directory or
 * typing a custom email commits a row. Shared by the intake review form and the
 * project-detail people editor.
 */
import { useState } from 'react';
import { X } from 'lucide-react';
import type { StatusUpdateRecipient } from '@vari-crew/schemas';
import type { Recipient } from '@/api/recipients';
import { RecipientAutocomplete } from '@/components/ui/RecipientAutocomplete';

export function OtherRecipientsEditor({
  value,
  onChange,
  recipients,
}: {
  value: StatusUpdateRecipient[];
  onChange: (next: StatusUpdateRecipient[]) => void;
  recipients: Recipient[];
}) {
  const [draft, setDraft] = useState('');
  function commit(name: string, email: string) {
    const e = email.trim();
    if (!e) return;
    if (!value.some((r) => r.email.toLowerCase() === e.toLowerCase())) {
      onChange([...value, { name: name.trim() || e, email: e }]);
    }
    setDraft('');
  }
  return (
    <div className="space-y-2">
      {value.map((r, i) => (
        <div key={`${r.email}-${i}`} className="flex items-center gap-2">
          <div className="flex-1 min-w-0 h-9 rounded-md border border-line bg-fill-soft/40 px-2.5 flex items-center text-sm">
            <span className="font-medium truncate">{r.name}</span>
            {r.email !== r.name && (
              <span className="text-ink-faint ml-1.5 truncate">· {r.email}</span>
            )}
          </div>
          <button
            type="button"
            onClick={() => onChange(value.filter((_, j) => j !== i))}
            className="text-ink-faint hover:text-ink shrink-0"
            aria-label={`Remove ${r.name}`}
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      ))}
      <RecipientAutocomplete
        value={draft}
        onChange={setDraft}
        onPick={(r) => commit(r.display_name, r.email)}
        recipients={recipients}
        allowCustomEmail
        placeholder="Add a person from the directory, or type a custom email…"
        className="h-9"
      />
    </div>
  );
}
